/* jshint browser:true */
import uuid from 'uuid/v4';
import DomUtils from '../dom-utils';
import PerfUtils from '../perf-utils';

/**
 * Management for flex menus.
 */
export default class FlexMenu {
  /**
   * Constructor.
   */
  constructor() {
    this.classNames = {
      isAutoClose: 'flex-menu--auto-close',
      isAutoOpen: 'flex-menu--auto-open',
      isMultiOpen: 'flex-menu--multi-open',
      isHamburger: 'flex-menu--hamburger',
      isHamburgerSmall: 'flex-menu--hamburger-sm',
      isHamburgerMedium: 'flex-menu--hamburger-md',
      isHamburgerLarge: 'flex-menu--hamburger-lg',
      isAutoHamburger: 'flex-menu--auto-hamburger',
      isSmartOverflow: 'flex-menu--smart-overflow',
      rootMenu: 'flex-menu__root-menu',
      submenu: 'flex-menu__submenu',
      menuItem: 'flex-menu__item',
      menuLink: 'flex-menu__link',
      menuToggle: 'flex-menu__menu-toggle',
      hasSubmenu: 'flex-menu__item--has-submenu',
      jsSubmenuIsOpen: 'js-flex-menu__item--open',
      jsHamburgerWrapper: 'js-flex-menu__hamburger-wrapper',
      jsHamburgerHomeItem: 'js-flex-menu__hamburger-home-item',
      jsHamburgerOpenButton: 'js-flex-menu__hamburger-open-button',
      jsHamburgerCloseButton: 'js-flex-menu__hamburger-close-button',
      jsHamburgerForce: 'js-flex-menu--hamburger-force',
      jsOverflowMoreItem: 'js-flex-menu__overflow-more-item',
    };

    this.rootClassNames = {
      jsHamburgerIsReady: 'js-flex-menu__hamburger--ready',
      jsHamburgerIsOpen: 'js-flex-menu__hamburger--open',
    };
  }

  /**
   * Initializes menus.
   *
   * @param {string} selector CSS selector to find menus. The selected
   *        elements should each contain a UL element as a descendant.
   */
  init(selector) {
    const wrappers = document.querySelectorAll(selector);

    if (!wrappers) {
      return;
    }

    wrappers.forEach((x) => this.setup(x));
  }

  /**
   * Sets up an individual menu.
   *
   * @param {HTMLElement} wrapper The top-level element of the menu.
   */
  setup(wrapper) {
    const menuItems = wrapper.querySelectorAll('li');

    this.addHandlers(menuItems, wrapper);
    this.autoOpen(wrapper);
    this.buildHamburgerMenu(wrapper);
    this.buildSmartOverflow(wrapper);
  }

  /**
   * Updates class names and ARIA attributes based on whether submenus are open
   * or closed.
   *
   * @param {NodeList} menuItems All menu items.
   * @param {HTMLElement} wrapper The top-level element of the menu.
   */
  addHandlers(menuItems, wrapper) {
    // Set up submenus and add listeners.
    menuItems.forEach((menuItem) => {
      const hasSubmenu = menuItem.classList
        .contains(this.classNames.hasSubmenu);

      if (!hasSubmenu) {
        // No handlers necessary.
        return;
      }

      const menuLink = menuItem.querySelector('.' + this.classNames.menuLink);
      const menuToggle = menuItem.querySelector(
        '.' + this.classNames.menuToggle,
      );
      const isEmptyLink = menuLink.href &&
        (menuLink.href.startsWith('javascript:') || menuLink.href === '#');

      // Add ARIA attributes.
      if (!menuToggle.hasAttribute('aria-haspopup')) {
        menuToggle.setAttribute('aria-haspopup', 'true');
      }

      if (!menuToggle.hasAttribute('aria-expanded')) {
        menuToggle.setAttribute('aria-expanded', 'false');
      }

      // Add listeners to change class name and update ARIA.
      const handleMenuToggleClick = (e) => {
        this.toggleSubmenu(wrapper, menuItem, menuLink);

        // Prevent link from activating if menu is closed or link is empty.
        const submenuIsOpen = menuItem.classList.contains(
          this.classNames.jsSubmenuIsOpen,
        );

        if (submenuIsOpen || isEmptyLink) {
          e.preventDefault();
          e.stopPropagation();
        }
      };

      menuLink.addEventListener('click', handleMenuToggleClick);
      menuToggle.addEventListener('click', handleMenuToggleClick);

      // Remove empty, top-level links from tab order and AT.
      if (isEmptyLink) {
        menuLink.setAttribute('aria-hidden', 'true');
        menuLink.setAttribute('tabindex', '-1');
      }
    });

    // Add listeners to close menus on Esc key press or outside click.
    document.body.addEventListener('keyup', (e) => {
      const isAutoClose = wrapper.classList.contains(
        this.classNames.isAutoClose,
      );

      if (!isAutoClose) {
        return;
      }

      if (e.key === 'Escape') {
        const allMenuItems = wrapper.querySelectorAll('li');

        allMenuItems.forEach((menuItem) => {
          this.closeMenuItem(menuItem);
        });
      }
    });

    document.body.addEventListener('click', (e) => {
      const isAutoClose = wrapper.classList.contains(
        this.classNames.isAutoClose,
      );

      if (!isAutoClose) {
        return;
      }

      if (!e.target.closest('.' + this.classNames.hasSubmenu)) {
        const allMenuItems = wrapper.querySelectorAll('li');

        allMenuItems.forEach((menuItem) => {
          this.closeMenuItem(menuItem);
        });
      }
    });
  }

  /**
   * Toggles the visibility of a menu item's submenu.
   *
   * @param {HTMLElement} wrapper The top-level element of the menu.
   * @param {Node} currentMenuItem The current menu item with a submenu.
   * @param {HTMLAnchorElement} menuLink The top-level link in the menu item.
   */
  toggleSubmenu(
    wrapper,
    currentMenuItem,
    menuLink,
  ) {
    const allMenuItems = wrapper.querySelectorAll('li');
    const isMultiOpen = wrapper.classList.contains(
      this.classNames.isMultiOpen,
    );
    const submenuIsOpen = currentMenuItem.classList.contains(
      this.classNames.jsSubmenuIsOpen,
    );

    if (!isMultiOpen) {
      // Mark all other menu items as closed.
      allMenuItems.forEach((menuItem) => {
        if (!menuItem.contains(menuLink)) {
          this.closeMenuItem(menuItem);
        }
      });
    } else {
      if (submenuIsOpen) {
        // Mark only descendant menus as closed.
        const descendants = currentMenuItem.querySelectorAll(
          '.' + this.classNames.hasSubmenu,
        );

        descendants.forEach((x) => this.closeMenuItem(x));
      }
    }

    // Toggle class names and ARIA.
    if (!submenuIsOpen) {
      this.openMenuItem(currentMenuItem);
    } else {
      this.closeMenuItem(currentMenuItem);
    }
  }

  /**
   * Updates class names and ARIA attributes to mark submenu as closed.
   *
   * @param {Element} menuItem The menu item to mark as closed.
   */
  closeMenuItem(menuItem) {
    if (!menuItem.classList.contains(this.classNames.hasSubmenu)) {
      return;
    }

    const menuToggle = menuItem.querySelector('.' + this.classNames.menuToggle);

    menuItem.classList.remove(this.classNames.jsSubmenuIsOpen);

    menuToggle.setAttribute('aria-expanded', 'false');
  }

  /**
   * Updates class names and ARIA attributes to mark submenu as open.
   *
   * @param {Element} menuItem The menu item to mark as open.
   */
  openMenuItem(menuItem) {
    if (!menuItem.classList.contains(this.classNames.hasSubmenu)) {
      return;
    }

    const menuToggle = menuItem.querySelector('.' + this.classNames.menuToggle);

    menuItem.classList.add(this.classNames.jsSubmenuIsOpen);

    menuToggle.setAttribute('aria-expanded', 'true');

    // Check for ancestor menus to open.
    const parentMenuItem = menuItem.parentElement.closest(
      '.' + this.classNames.hasSubmenu,
    );

    if (parentMenuItem !== null) {
      this.openMenuItem(parentMenuItem);
    }
  }

  /**
   * Opens the menu to the level containing the current page URL path.
   *
   * @param {HTMLElement} wrapper The top-level element of the menu.
   */
  autoOpen(wrapper) {
    const isAutoOpen = wrapper.classList.contains(this.classNames.isAutoOpen);

    if (!isAutoOpen) {
      return;
    }

    const urlPath = window.location.pathname;
    const links = wrapper.querySelectorAll('a[href]');

    for (const link of links) {
      if (
        link.getAttribute('aria-current') === 'page' ||
        urlPath === link.pathname
      ) {
        const menuItem = link.closest('.' + this.classNames.hasSubmenu);

        if (menuItem !== null) {
          this.openMenuItem(menuItem);
          break;
        }
      }
    }
  }

  /**
   * Adds HTML elements and event listeners for converting the menu into a
   * hamburger/mobile menu.
   *
   * @param {HTMLElement} wrapper The top-level element of the menu.
   */
  buildHamburgerMenu(wrapper) {
    const isHamburger = Array.from(wrapper.classList)
      .some((className) => {
        return [
          this.classNames.isHamburger,
          this.classNames.isHamburgerSmall,
          this.classNames.isHamburgerMedium,
          this.classNames.isHamburgerLarge,
        ].includes(className);
      });

    if (!isHamburger) {
      // No need to implement hamburger structures.
      return;
    }

    // Wrap menu in another wrapper.
    const rootMenu = wrapper.querySelector('.' + this.classNames.rootMenu);
    const menuWrapper = document.createElement('div');

    menuWrapper.className = this.classNames.jsHamburgerWrapper;
    menuWrapper.appendChild(rootMenu);

    wrapper.appendChild(menuWrapper);

    this.addHamburgerHomeLink(wrapper, rootMenu);
    this.addHamburgerMenuButton(wrapper, rootMenu);

    const closeButton = this.addHamburgerCloseButton(wrapper, rootMenu);

    // Close menu on outside click.
    document.addEventListener('click', (e) => {
      if (
        !document.documentElement.classList.contains(
          this.rootClassNames.jsHamburgerIsOpen,
        )
      ) {
        // Menu is already closed.
        return;
      }

      if (
        e.target.closest('.' + this.classNames.jsHamburgerWrapper) ||
        e.target.classList.contains(this.classNames.jsHamburgerOpenButton)
      ) {
        // Click was inside the menu or on the open menu button
        return;
      }

      closeButton.click();
    });

    // Close menu on window resize.
    window.addEventListener('resize', PerfUtils.debounce(() => {
      if (
        !document.documentElement.classList.contains(
          this.rootClassNames.jsHamburgerIsOpen,
        )
      ) {
        return;
      }

      closeButton.click();
    }, 100));

    // Add class to root element to indicate hamburger is ready.
    const root = document.getElementsByTagName('html')[0];

    root.classList.add(this.rootClassNames.jsHamburgerIsReady);
  }

  /**
   * Inserts a link to the home page as the first link of the menu.
   *
   * @param {HTMLElement} wrapper The top-level element of the menu.
   * @param {HTMLUListElement} rootMenu The UL of the root menu.
   *
   * @return {HTMLLIElement} The LI that contains the home link.
   */
  addHamburgerHomeLink(wrapper, rootMenu) {
    const linkText = wrapper.dataset.hamburgerHomeText || 'Home';
    const linkUri = wrapper.dataset.hamburgerHomeUri || '/';
    const menuItem = document.createElement('li');
    const menuLink = document.createElement('a');

    menuItem.classList.add(this.classNames.menuItem);
    menuItem.classList.add(this.classNames.jsHamburgerHomeItem);

    menuLink.classList.add(this.classNames.menuLink);
    menuLink.setAttribute('href', linkUri);
    menuLink.innerText = linkText;

    menuItem.appendChild(menuLink);

    rootMenu.insertAdjacentElement('afterbegin', menuItem);

    return menuItem;
  }

  /**
   * Adds a button that opens the hamburger menu.
   *
   * @param {HTMLElement} wrapper The top-level element of the menu.
   * @param {HTMLUListElement} rootMenu The UL of the root menu.
   *
   * @return {HTMLButtonElement} The newly created menu button.
   */
  addHamburgerMenuButton(wrapper, rootMenu) {
    const root = document.getElementsByTagName('html')[0];

    // Ensure the rootMenu has an ID.
    if (!rootMenu.id) {
      rootMenu.id = uuid();
    }

    // Build the button.
    const openText = wrapper.dataset.hamburgerOpenText || 'Open Menu';
    const menuButton = document.createElement('button');

    menuButton.id = uuid();
    menuButton.type = 'button';
    menuButton.className = this.classNames.jsHamburgerOpenButton;
    menuButton.setAttribute('aria-haspopup', 'true');
    menuButton.setAttribute('aria-controls', rootMenu.id);
    menuButton.innerText = openText;

    rootMenu.setAttribute('aria-labelledby', menuButton.id);

    // Add event handlers.
    menuButton.addEventListener('click', () => {
      // Toggle class on root element so styles can show the menu.
      root.classList.toggle(this.rootClassNames.jsHamburgerIsOpen);

      // Move focus to the close button (for AT users).
      const closeButton = document.querySelector(
        '.' + this.classNames.jsHamburgerCloseButton,
      );

      if (closeButton !== null) {
        closeButton.focus();
      }
    });

    // Insert the button before the menu wrapper.
    wrapper.insertAdjacentElement('afterbegin', menuButton);

    return menuButton;
  }

  /**
   * Adds a button that closes the hamburger menu.
   *
   * @param {HTMLElement} wrapper The top-level element of the menu.
   * @param {HTMLUListElement} rootMenu The UL of the root menu.
   *
   * @return {HTMLButtonElement} The newly created close button.
   */
  addHamburgerCloseButton(wrapper, rootMenu) {
    const root = document.getElementsByTagName('html')[0];

    // Build the button.
    const closeText = wrapper.dataset.hamburgerCloseText || 'Close Menu';
    const closeButton = document.createElement('button');

    closeButton.type = 'button';
    closeButton.className = this.classNames.jsHamburgerCloseButton;
    closeButton.innerText = closeText;

    // Add event handlers.
    closeButton.addEventListener('click', () => {
      // Remove class on root element so styles can hide the menu.
      root.classList.remove(this.rootClassNames.jsHamburgerIsOpen);

      // Move focus to the menu button (for AT users).
      const menuButton = document.querySelector(
        '.' + this.classNames.jsHamburgerOpenButton,
      );

      if (menuButton !== null) {
        menuButton.focus();
      }
    });

    // Insert the button before the root menu.
    rootMenu.parentElement.insertAdjacentElement('afterbegin', closeButton);

    return closeButton;
  }

  /**
   * Sets up structure and handlers for adding a "More" submenu if a menu is too
   * wide to fit in the available space.
   *
   * @param {HTMLElement} wrapper The top-level element of the menu.
   */
  buildSmartOverflow(wrapper) {
    if (!('ResizeObserver' in window)) {
      // Doesn't support ResizeObserver.
      return;
    }

    const isSmartOverflow = Array.from(wrapper.classList).some((x) => {
      return x === this.classNames.isSmartOverflow ||
        x === this.classNames.isAutoHamburger;
    });

    if (!isSmartOverflow) {
      // Doesn't use smart overflow.
      return;
    }

    // Set up ResizeObserver for when the menu wrapper's width changes.
    const rootMenu = wrapper.querySelector('.' + this.classNames.rootMenu);
    let observeStart = false;

    const resizeObserver = new ResizeObserver(() => {
      if (!observeStart) {
        // Make no changes on the first observation. This is because we will
        // alter the menu, triggering ResizeObserver to fire infinitely.
        observeStart = true;
        return;
      }

      resizeObserver.disconnect();

      this.destroySmartOverflow(wrapper, rootMenu);
      const isOverflowing = this.initSmartOverflow(wrapper, rootMenu);

      observeStart = false;
      requestAnimationFrame(() => resizeObserver.observe(wrapper));

      if (isOverflowing) {
        // Prevent ResizeObserver from running when "More" menu is clicked.
        rootMenu.querySelector('.' + this.classNames.jsOverflowMoreItem)
          .addEventListener('click', () => observeStart = false);
      }
    });

    // Initialize smart overflow.
    this.initSmartOverflow(wrapper, rootMenu);

    // Watch for resizes.
    resizeObserver.observe(wrapper);
  }

  /**
   * Restores the menu to the state before smart overflow was implemented.
   *
   * @param {HTMLElement} wrapper The top-level element of the menu.
   * @param {HTMLUListElement} rootMenu The UL of the root menu.
   */
  destroySmartOverflow(wrapper, rootMenu) {
    const overflowMenuItem = rootMenu.lastChild;
    const isHamburgerOpen = document.documentElement.classList.contains(
      this.rootClassNames.jsHamburgerIsOpen,
    );

    // Remove forced hamburger menu.
    if (!isHamburgerOpen) {
      wrapper.classList.remove(this.classNames.jsHamburgerForce);
    }

    if (
      !overflowMenuItem.classList ||
      !overflowMenuItem.classList.contains(this.classNames.jsOverflowMoreItem)
    ) {
      // Overflow menu doesn't exist.
      return;
    }

    // Add menu items back.
    const submenu = overflowMenuItem.querySelector(
      '.' + this.classNames.submenu,
    );

    while (submenu.children.length) {
      overflowMenuItem.insertAdjacentElement('beforebegin', submenu.firstChild);
      overflowMenuItem.parentElement.insertBefore(
        document.createTextNode(' '),
        overflowMenuItem,
      );
    }

    // Remove overflowMenuItem.
    overflowMenuItem.remove();
  }

  /**
   * Adds overflowing menu items to a "More" menu if necessary.
   *
   * @param {HTMLElement} wrapper The top-level element of the menu.
   * @param {HTMLUListElement} rootMenu The UL of the root menu.
   *
   * @return {bool} Whether the "More" menu was implemented.
   */
  initSmartOverflow(wrapper, rootMenu) {
    if (!DomUtils.isVisible(rootMenu)) {
      // The menu isn't being shown currently.
      return false;
    }

    const isHamburgerOpen = document.documentElement.classList.contains(
      this.rootClassNames.jsHamburgerIsOpen,
    );
    const menuDisplay = getComputedStyle(rootMenu).getPropertyValue('display');

    if (isHamburgerOpen || menuDisplay === 'block') {
      // Smart overflow is not compatible with vertical view or hamburger menu.
      return false;
    }

    // Exit early if no items overflow.
    const overflowCount = this.getOverflowCount(wrapper, rootMenu);

    if (overflowCount === 0) {
      return false;
    }

    // Use hamburger menu instead of "More" menu if using auto-hamburger.
    const isAutoHamburger = wrapper.classList.contains(
      this.classNames.isAutoHamburger,
    );

    if (isAutoHamburger && overflowCount > 0) {
      wrapper.classList.add(this.classNames.jsHamburgerForce);
      wrapper.classList.add(this.classNames.isHamburgerSmall);

      return false;
    }

    // Check number of menu items to remove.
    const menuItemCount = rootMenu.children.length;

    if (menuItemCount - overflowCount <= 0) {
      // All items would have to be removed. Leave it alone.
      return false;
    }

    // Move menu items into an overflow menu.
    const submenu = document.createElement('ul');

    submenu.id = uuid();
    submenu.className = this.classNames.submenu;

    for (
      let n = menuItemCount - 1; n > menuItemCount - overflowCount - 1; n--
    ) {
      submenu.insertAdjacentElement('afterbegin', rootMenu.children[n]);
    }

    const moreText = wrapper.dataset.overflowMoreText || 'More';
    const overflowMenuItem = this.getOverflowMenuItem(
      wrapper,
      moreText,
      submenu,
    );

    rootMenu.appendChild(overflowMenuItem);

    return true;
  }

  /**
   * Returns the number of menu items that are overflowing.
   *
   * @param {HTMLElement} wrapper The top-level element of the menu.
   * @param {HTMLUListElement} rootMenu The UL of the root menu.
   *
   * @return {number} The number of overflowing menu items.
   */
  getOverflowCount(wrapper, rootMenu) {
    const rootMenuClone = rootMenu.cloneNode(true);
    let isOverflowMenuItemAdded = false;

    const adjustMenuAndGetCount = (counter = 0) => {
      if (rootMenuClone.children.length <= 1) {
        // No menu items available.
        return counter;
      }

      if (DomUtils.isOverflowing(rootMenuClone)) {
        // Remove last link from menu, add "More" menu, and try again.
        if (!isOverflowMenuItemAdded) {
          const moreText = wrapper.dataset.overflowMoreText || 'More';
          const overflowMenuItem = this.getOverflowMenuItem(wrapper, moreText);

          rootMenuClone.removeChild(rootMenuClone.lastElementChild);
          rootMenuClone.appendChild(overflowMenuItem);

          isOverflowMenuItemAdded = true;
        } else {
          rootMenuClone.removeChild(
            rootMenuClone.children[rootMenuClone.childElementCount - 2]);
        }

        return adjustMenuAndGetCount(counter + 1);
      }

      return counter;
    };

    // Hide menu and use a clone of it for operations
    rootMenu.setAttribute('hidden', '');
    rootMenu.insertAdjacentElement('beforebegin', rootMenuClone);

    // Remove items until the menu no longer overflows
    const count = adjustMenuAndGetCount();

    // Remove clone and show original menu
    rootMenuClone.remove();
    rootMenu.removeAttribute('hidden');

    return count;
  }

  /**
   * Creates and returns the "More" menu item, link, and submenu toggle button.
   * If a submenu is provided, it will be appended as well.
   *
   * @param {HTMLElement} wrapper The top-level element of the menu.
   * @param {string} moreText The text to use for the "More" link.
   * @param {HTMLUListElement} submenu The UL of the submenu to append.
   *
   * @return {HTMLLIElement} The LI wrapper for the new menu item.
   */
  getOverflowMenuItem(wrapper, moreText, submenu) {
    const menuItem = document.createElement('li');
    const menuLink = document.createElement('a');
    const toggleButton = document.createElement('button');

    // Build menu item.
    menuItem.classList.add(this.classNames.menuItem);
    menuItem.classList.add(this.classNames.hasSubmenu);
    menuItem.classList.add(this.classNames.jsOverflowMoreItem);

    menuLink.href = 'javascript:void(0)';
    menuLink.className = this.classNames.menuLink;
    menuLink.innerText = moreText;
    menuLink.setAttribute('aria-hidden', 'true');
    menuLink.setAttribute('tabindex', '-1');

    toggleButton.className = this.classNames.menuToggle;
    toggleButton.innerText = moreText;
    toggleButton.setAttribute('aria-haspopup', 'true');
    toggleButton.setAttribute('aria-expanded', 'false');

    menuItem.appendChild(menuLink);
    menuItem.appendChild(toggleButton);

    if (!submenu) {
      return menuItem;
    }

    // Set up and append submenu.
    toggleButton.setAttribute('aria-controls', submenu.id);

    menuItem.appendChild(submenu);

    menuLink.addEventListener('click', (e) => {
      this.toggleSubmenu(wrapper, menuItem, menuLink);

      e.preventDefault();
      e.stopPropagation();
    });

    toggleButton.addEventListener('click', (e) => {
      this.toggleSubmenu(wrapper, menuItem, menuLink);
    });

    return menuItem;
  }
}
